import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Card from '../../Card/Card';
import ColorPicker from '../../FormElements/ColorPicker/ColorPicker';
import TabActionButtons from '../../FormElements/TabActionButtons/TabActionButtons';

import styles from './TabSections.module.css';

const DesignSettings = ({ onSave, onReset, settingsData, onSettingsChange, showToast }) => {
  const [settings, setSettings] = useState({
    hvnly_brandColor: '#6C60FE',
    hvnly_secondaryColor: '#764ba2',
    hvnly_linkColor: '#1E1E2F',
    hvnly_linkHoverColor: '#764ba2',
    hvnly_titleColor: '#1E1E2F',
    hvnly_primaryTextColor: '#1E1E2F',
    hvnly_secondaryTextColor: '#555555',
    hvnly_bodyFontSize: 16,
    hvnly_bodyFontWeights: '400',
    hvnly_titleFontWeights: '600',
    hvnly_bodyColor: '#333333'
  });

  useEffect(() => {
    if (settingsData && typeof settingsData === 'object' && Object.keys(settingsData).length > 0) {
      const updatedSettings = {
        hvnly_brandColor: settingsData.hvnly_brandColor || settingsData.brand_color || '#6C60FE',
        hvnly_secondaryColor: settingsData.hvnly_secondaryColor || settingsData.secondary_color || '#764ba2',
        hvnly_linkColor: settingsData.hvnly_linkColor || settingsData.link_color || '#1E1E2F',
        hvnly_linkHoverColor: settingsData.hvnly_linkHoverColor || settingsData.link_hover_color || '#764ba2',
        hvnly_titleColor: settingsData.hvnly_titleColor || settingsData.title_color || '#1E1E2F',
        hvnly_primaryTextColor: settingsData.hvnly_primaryTextColor || settingsData.primary_text_color || '#1E1E2F',
        hvnly_secondaryTextColor: settingsData.hvnly_secondaryTextColor || settingsData.secondary_text_color || '#555555',
        hvnly_bodyFontSize: settingsData.hvnly_bodyFontSize || settingsData.body_font_size || 16,
        hvnly_bodyFontWeights: settingsData.hvnly_bodyFontWeights || settingsData.body_font_weights || '400',
        hvnly_titleFontWeights: settingsData.hvnly_titleFontWeights || settingsData.title_font_weights || '600',
        hvnly_bodyColor: settingsData.hvnly_bodyColor || settingsData.body_color || '#333333'
      };
      setSettings(prev => ({ ...prev, ...updatedSettings }));
    }
  }, [settingsData]);

  const handleInputChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(settings);
      return true;
    }
    return false;
  };

  const handleReset = async () => {
    const defaultSettings = {
      hvnly_brandColor: '#6C60FE',
      hvnly_secondaryColor: '#764ba2',
      hvnly_linkColor: '#1E1E2F',
      hvnly_linkHoverColor: '#764ba2',
      hvnly_titleColor: '#1E1E2F',
      hvnly_primaryTextColor: '#1E1E2F',
      hvnly_secondaryTextColor: '#555555',
      hvnly_bodyFontSize: 16,
      hvnly_bodyFontWeights: '400',
      hvnly_titleFontWeights: '600',
      hvnly_bodyColor: '#333333'
    };
    setSettings(defaultSettings);
    if (onSettingsChange) {
      onSettingsChange(defaultSettings);
    }
    if (onReset) {
      await onReset();
      return true;
    }
    return false;
  };

  return (
    <div 
      className={`${styles['hvnly-settings-api__content']} ${styles['hvnly-settings-api__content--active']}`} 
      id="design"
    >
      <h2>{__('Global Colors', 'havenlytics')}</h2>

      <Card title={__("Global Style", "havenlytics")} icon="fas fa-paintbrush" collapsible>
        <ColorPicker
          label={__("Brand/Primary Color", "havenlytics")}
          value={settings.hvnly_brandColor}
          onChange={value => handleInputChange('hvnly_brandColor', value)}
          defaultColor="#6C60FE"
        />
        <ColorPicker
          label={__("Secondary Color", "havenlytics")}
          value={settings.hvnly_secondaryColor}
          onChange={value => handleInputChange('hvnly_secondaryColor', value)}
          defaultColor="#764ba2"
        />
        <ColorPicker
          label={__("Link Color", "havenlytics")}
          value={settings.hvnly_linkColor}
          onChange={value => handleInputChange('hvnly_linkColor', value)}
          defaultColor="#1E1E2F"
        />
        <ColorPicker
          label={__("Link Hover Color", "havenlytics")}
          value={settings.hvnly_linkHoverColor}
          onChange={value => handleInputChange('hvnly_linkHoverColor', value)}
          defaultColor="#764ba2"
        />
        <ColorPicker
          label={__("Title Text Color", "havenlytics")}
          value={settings.hvnly_titleColor}
          onChange={value => handleInputChange('hvnly_titleColor', value)}
          defaultColor="#1E1E2F"
        />
        <ColorPicker
          label={__("Primary Text Color", "havenlytics")}
          value={settings.hvnly_primaryTextColor}
          onChange={value => handleInputChange('hvnly_primaryTextColor', value)}
          defaultColor="#1E1E2F"
        />
        <ColorPicker
          label={__("Secondary Text Color", "havenlytics")}
          value={settings.hvnly_secondaryTextColor}
          onChange={value => handleInputChange('hvnly_secondaryTextColor', value)}
          defaultColor="#555555"
        />
      </Card>

      <TabActionButtons
        onSave={handleSave}
        onReset={handleReset}
        showToast={showToast}
        saveLabel={__("Save Design Settings", "havenlytics")}
        resetLabel={__("Reset Changes", "havenlytics")}
      />
    </div>
  );
};

export default DesignSettings;